import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue
} from "@/components/ui/select";

const today = () => new Date().toISOString().slice(0, 10);
const money = v => "Rp" + Number(v || 0).toLocaleString("id-ID");

export default function CashBank() {
  const [tab, setTab] = useState("accounts");
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [coa, setCoa] = useState([]);
  const [busy, setBusy] = useState(false);

  const [account, setAccount] = useState({
    name: "", type: "cash", coa_code: "",
    bank_name: "", account_number: ""
  });

  const [tx, setTx] = useState({
    date: today(), account_id: "",
    counterpart_coa_code: "", amount: "", description: ""
  });

  const [transfer, setTransfer] = useState({
    date: today(), source_account_id: "",
    destination_account_id: "", amount: "", description: ""
  });

  const [opening, setOpening] = useState({
    date: today(), account_id: "",
    equity_coa_code: "", amount: ""
  });

  const load = async () => {
    try {
      const [a, t, c] = await Promise.all([
        api.get("/cash-bank/accounts"),
        api.get("/cash-bank/transactions"),
        api.get("/coa")
      ]);
      setAccounts(a.data || []);
      setTransactions(t.data || []);
      setCoa(c.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memuat data");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (url, data, message) => {
    setBusy(true);
    try {
      await api.post(url, data);
      toast.success(message);
      await load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transaksi gagal");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const activeAccounts = accounts.filter(x => x.active);
  const accountOptions = activeAccounts.map(x => ({
    value: x.id, label: x.name
  }));
  const coaOptions = coa.filter(x => x.active !== false).map(x => ({
    value: x.code, label: `${x.code} — ${x.name}`
  }));

  const field = (label, value, setter, type = "text") => (
    <label className="grid gap-1.5 text-sm">
      <span className="font-medium">{label}</span>
      <Input type={type} value={value ?? ""}
        onChange={e => setter(e.target.value)} />
    </label>
  );

  const select = (label, value, setter, options) => (
    <label className="grid gap-1.5 text-sm">
      <span className="font-medium">{label}</span>
      <Select value={value || ""} onValueChange={setter}>
        <SelectTrigger><SelectValue placeholder="Pilih..." /></SelectTrigger>
        <SelectContent>
          {options.map(x => (
            <SelectItem key={x.value} value={String(x.value)}>
              {x.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );

  const tabs = [
    ["accounts", "Daftar Rekening"],
    ["opening", "Saldo Awal"],
    ["in", "Kas Masuk"],
    ["out", "Kas Keluar"],
    ["transfer", "Transfer"],
    ["history", "Riwayat"]
  ];

  return (
    <div className="page-shell space-y-5">
      <div>
        <p className="text-xs text-muted-foreground">
          Finance & Accounting / Kas & Bank
        </p>
        <h1 className="text-2xl font-bold mt-2">Kas & Bank</h1>
        <p className="text-sm text-muted-foreground">
          Pengelolaan rekening, saldo awal, penerimaan, pengeluaran, dan transfer.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b pb-3">
        {tabs.map(([key, label]) => (
          <Button key={key} size="sm"
            variant={tab === key ? "default" : "outline"}
            onClick={() => setTab(key)}>
            {label}
          </Button>
        ))}
      </div>

      {tab === "accounts" && (
        <Card className="p-5 space-y-4">
          <h2 className="font-semibold">Tambah Rekening</h2>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {field("Nama rekening", account.name,
              v => setAccount({...account, name:v}))}
            {select("Jenis rekening", account.type,
              v => setAccount({...account, type:v}), [
                {value:"cash",label:"Kas"},
                {value:"bank",label:"Bank"}
              ])}
            {select("Akun COA", account.coa_code,
              v => setAccount({...account, coa_code:v}), coaOptions)}
            {field("Nama bank", account.bank_name,
              v => setAccount({...account, bank_name:v}))}
            {field("Nomor rekening", account.account_number,
              v => setAccount({...account, account_number:v}))}
          </div>
          <Button disabled={busy}
            onClick={() => save("/cash-bank/accounts", account, "Rekening disimpan")}>
            Simpan Rekening
          </Button>

          <h2 className="font-semibold pt-4">Daftar Rekening</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left">
                <th className="p-3">Nama</th>
                <th className="p-3">Jenis</th>
                <th className="p-3">COA</th>
                <th className="p-3">Bank / Nomor</th>
                <th className="p-3">Status</th>
              </tr></thead>
              <tbody>{accounts.map(x => (
                <tr key={x.id} className="border-b">
                  <td className="p-3">{x.name}</td>
                  <td className="p-3">{x.type}</td>
                  <td className="p-3">{x.coa_code} — {x.coa_name}</td>
                  <td className="p-3">{x.bank_name} {x.account_number}</td>
                  <td className="p-3">{x.active ? "Aktif" : "Nonaktif"}</td>
                </tr>
              ))}</tbody>
            </table>
            {!accounts.length && <p className="text-center text-sm text-muted-foreground py-5">Belum ada rekening.</p>}
          </div>
        </Card>
      )}

      {tab === "opening" && (
        <Card className="p-5 space-y-4">
          <h2 className="font-semibold">Saldo Awal Rekening</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {field("Tanggal", opening.date,
              v => setOpening({...opening,date:v}), "date")}
            {select("Rekening", opening.account_id,
              v => setOpening({...opening,account_id:v}), accountOptions)}
            {select("Akun lawan saldo awal", opening.equity_coa_code,
              v => setOpening({...opening,equity_coa_code:v}), coaOptions)}
            {field("Nominal", opening.amount,
              v => setOpening({...opening,amount:v}), "number")}
          </div>
          <Button disabled={busy}
            onClick={() => save("/cash-bank/opening-balance", opening, "Saldo awal dicatat")}>
            Simpan Saldo Awal
          </Button>
        </Card>
      )}

      {(tab === "in" || tab === "out") && (
        <Card className="p-5 space-y-4">
          <h2 className="font-semibold">
            {tab === "in" ? "Kas Masuk" : "Kas Keluar"}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {field("Tanggal", tx.date,
              v => setTx({...tx,date:v}), "date")}
            {select("Rekening", tx.account_id,
              v => setTx({...tx,account_id:v}), accountOptions)}
            {select("Akun lawan", tx.counterpart_coa_code,
              v => setTx({...tx,counterpart_coa_code:v}), coaOptions)}
            {field("Nominal", tx.amount,
              v => setTx({...tx,amount:v}), "number")}
            {field("Keterangan", tx.description,
              v => setTx({...tx,description:v}))}
          </div>
          <Button disabled={busy} onClick={() => save(
            tab === "in" ? "/cash-bank/cash-in" : "/cash-bank/cash-out",
            tx, "Transaksi berhasil dicatat"
          )}>Simpan Transaksi</Button>
        </Card>
      )}

      {tab === "transfer" && (
        <Card className="p-5 space-y-4">
          <h2 className="font-semibold">Transfer Antar Rekening</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {field("Tanggal", transfer.date,
              v => setTransfer({...transfer,date:v}), "date")}
            {select("Rekening sumber", transfer.source_account_id,
              v => setTransfer({...transfer,source_account_id:v}), accountOptions)}
            {select("Rekening tujuan", transfer.destination_account_id,
              v => setTransfer({...transfer,destination_account_id:v}), accountOptions)}
            {field("Nominal", transfer.amount,
              v => setTransfer({...transfer,amount:v}), "number")}
            {field("Keterangan", transfer.description,
              v => setTransfer({...transfer,description:v}))}
          </div>
          <Button disabled={busy}
            onClick={() => save("/cash-bank/transfer", transfer, "Transfer berhasil dicatat")}>
            Simpan Transfer
          </Button>
        </Card>
      )}

      {tab === "history" && (
        <Card className="p-5">
          <h2 className="font-semibold mb-4">Riwayat Transaksi</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left">
                <th className="p-3">Tanggal</th>
                <th className="p-3">Referensi</th>
                <th className="p-3">Jenis</th>
                <th className="p-3">Rekening</th>
                <th className="p-3">Keterangan</th>
                <th className="p-3 text-right">Nominal</th>
              </tr></thead>
              <tbody>{transactions.map(x => (
                <tr key={x.id} className="border-b">
                  <td className="p-3">{x.date}</td>
                  <td className="p-3">{x.reference}</td>
                  <td className="p-3">{x.kind}</td>
                  <td className="p-3">
                    {x.account_name || `${x.source_account_name} → ${x.destination_account_name}`}
                  </td>
                  <td className="p-3">{x.description}</td>
                  <td className="p-3 text-right">{money(x.amount)}</td>
                </tr>
              ))}</tbody>
            </table>
            {!transactions.length && <p className="text-center text-muted-foreground py-5">Belum ada transaksi.</p>}
          </div>
        </Card>
      )}
    </div>
  );
}
